import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

const fmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const organizationReportService = {
  fetchReport,
  fetchFacilityReport,
  fetchOrgReport,
  fetchOrganizations,
  fetchOrgFacilityIds,
  fetchGroups,
  fetchGroupReport,
};

// MAXMRJ-admin Executive Dashboard group picker — all groups (flat). Each carries groupType
// (EXECUTIVE/SVPO/VPO/ADMIN) so the UI can build the Group Type + Group selectors.
async function fetchGroups() {
  const response = await fetch(apiPath.groups, {
    method: "GET",
    headers: generalAuthTokenHeader(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch groups (HTTP ${response.status})`);
  }
  return response.json();
}

// Report over a group's whole subtree (GROUP + DIRECT facilities, recursively) — one call;
// facilities are resolved server-side from the group id. Returns the OrganizationReportDto shape.
async function fetchGroupReport({ groupId, startDate, endDate }) {
  const params = [`from=${fmt(startDate)}`, `to=${fmt(endDate)}`].join("&");
  const response = await fetch(`${apiPath.groups}/${groupId}/report?${params}`, {
    method: "GET",
    headers: generalAuthTokenHeader(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch group report (HTTP ${response.status})`);
  }
  return response.json();
}

// MAXMRJ-admin Executive Dashboard org picker — list ALL organizations (large page so the
// dropdown isn't truncated by the default page size). Returns OrganizationDTO[] {id, name, ...}.
async function fetchOrganizations() {
  const response = await fetch(`${apiPath.organizations}?page=0&size=1000&sort=name,asc`, {
    method: "GET",
    headers: generalAuthTokenHeader(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch organizations (HTTP ${response.status})`);
  }
  return response.json();
}

// Facilities under an organization (the same set the Org Admin report runs over:
// findByOrganizationId). Returns just the facility ids for the report query.
async function fetchOrgFacilityIds(orgId) {
  const response = await fetch(`${apiPath.organizations}/${orgId}/facilities`, {
    method: "GET",
    headers: generalAuthTokenHeader(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch organization facilities (HTTP ${response.status})`);
  }
  const facilities = await response.json();
  return Array.isArray(facilities)
    ? facilities.map((f) => f.facilityId).filter((id) => id != null)
    : [];
}

// Org Admin Executive Dashboard — all facilities under the caller's org (resolved
// server-side). Guarded endpoint; no facility/org id sent by the client.
async function fetchOrgReport({ startDate, endDate }) {
  const params = [`from=${fmt(startDate)}`, `to=${fmt(endDate)}`].join("&");
  const response = await fetch(`${apiPath.orgAdminReport}?${params}`, {
    method: "GET",
    headers: generalAuthTokenHeader(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch organization report (HTTP ${response.status})`);
  }
  return response.json();
}

// Facility-admin single-facility report — guarded endpoint, facility fixed by path var.
async function fetchFacilityReport({ facilityId, startDate, endDate }) {
  const params = [`from=${fmt(startDate)}`, `to=${fmt(endDate)}`].join("&");
  const response = await fetch(
    `${apiPath.facilityAdminFacilities}/${facilityId}/report?${params}`,
    { method: "GET", headers: generalAuthTokenHeader() }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch facility report (HTTP ${response.status})`);
  }
  return response.json();
}

async function fetchReport({ facilityIds, startDate, endDate }) {
  const params = [
    `facilityIds=${facilityIds.join(",")}`,
    `from=${fmt(startDate)}`,
    `to=${fmt(endDate)}`,
  ].join("&");

  const response = await fetch(`${apiPath.organizationReport}?${params}`, {
    method: "GET",
    headers: generalAuthTokenHeader(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch organization report (HTTP ${response.status})`);
  }
  return response.json();
}
